import {useEffect, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {Table, Tag, Alert, Select, Divider, Typography, App as AntdApp} from "antd";
import {Link} from "react-router-dom";
import dayjs from "dayjs";
import {PageHeader, Loading, FontAwesomeIcon, Button, ModalForm} from "~/components";
import {NumberField, DateField} from "~/components/Forms";
import {useGetServiceQuotasQuery, useUpdateServiceBudgetsMutation} from "~/reduxs/api/serviceQuotaApiSlice";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {nf, compact} from "../util";
import style from "../style/Member.module.scss";
import className from "classnames/bind";

const cn = className.bind(style);

/** Icon theo dịch vụ (khớp key BE trả về). */
const SERVICE_ICONS = {
	ai: 'fa-light fa-wand-magic-sparkles',
	dataforseo: 'fa-light fa-database',
	google_ads: 'fa-light fa-rectangle-ad',
	google_data: 'fa-light fa-chart-column',
	pagespeed: 'fa-light fa-gauge-high',
	email: 'fa-light fa-envelope',
};

/** Tiền theo đơn vị tài khoản dịch vụ: VND → 1.234đ · USD → $1.23. */
const fmtMoney = (v, currency) => currency === 'usd'
	? `$${Number(v || 0).toLocaleString('en-US', {maximumFractionDigits: 2})}`
	: `${nf(Math.round(v || 0))}đ`;

const monthLabel = (ym) => dayjs(`${ym}-01`).format('MM/YYYY');

/** Thẻ chỉ số nhỏ (mirror StatCard của AdminStats — dùng lớp .stat sẵn có). */
function StatCard({icon, value, label, color}) {
	return (
		<div className={cn('stat')}>
			<div className={cn('stat-icon')}><FontAwesomeIcon icon={icon} /></div>
			<div className={cn('stat-body')}>
				<div className={cn('stat-value')} style={color ? {color} : undefined}>{value}</div>
				<div className={cn('stat-label')}>{label}</div>
			</div>
		</div>
	);
}

/** Khối chuẩn đoán TIỀN của 1 tài khoản trả phí (đầu kỳ → đã chi → còn lại → cần nạp). */
function BudgetPanel({budget, onEdit}) {
	if (!budget) return null;

	const cur = budget.currency;

	if (!budget.configured) {
		return (
			<Alert
				type="info"
				showIcon
				style={{marginBottom: 12}}
				message={
					<span>
						Chưa nhập số dư đầu kỳ của tài khoản này — tốc độ chi ước tính {fmtMoney(budget.burn_month_cost, cur)}/tháng.{' '}
						<Typography.Link onClick={onEdit}>Nhập số dư đầu kỳ</Typography.Link> để hệ thống chuẩn đoán còn lại bao nhiêu và cần nạp thêm bao nhiêu.
					</span>
				}
			/>
		);
	}

	const statusMeta = {
		ok: {type: 'success', text: 'An toàn'},
		warning: {type: 'warning', text: 'Sắp cạn — nên nạp thêm'},
		danger: {type: 'error', text: 'Đã cạn / âm — cần nạp ngay'},
	}[budget.status] || {type: 'info', text: ''};

	return (
		<>
			<div className={cn('stats')}>
				<StatCard icon="fa-light fa-piggy-bank" value={fmtMoney(budget.opening, cur)} label={`Đầu kỳ (nạp ${dayjs(budget.opening_date).format('DD/MM/YYYY')})`} />
				<StatCard icon="fa-light fa-fire" value={fmtMoney(budget.spent, cur)} label="Đã chi từ đầu kỳ (ước tính)" />
				<StatCard
					icon="fa-light fa-battery-half"
					value={fmtMoney(budget.remaining, cur)}
					label={budget.months_left !== null ? `Còn lại — đủ dùng ~${budget.months_left} tháng` : 'Còn lại'}
					color={budget.remaining <= 0 ? '#dc2626' : undefined}
				/>
				<StatCard
					icon="fa-light fa-vault"
					value={budget.recommended_topup > 0 ? fmtMoney(budget.recommended_topup, cur) : 'Không cần'}
					label="Cần nạp thêm để an toàn"
					color={budget.recommended_topup > 0 ? '#d97706' : '#16a34a'}
				/>
			</div>
			<Alert
				type={statusMeta.type}
				showIcon
				style={{marginBottom: 12}}
				message={
					<span>
						<b>{statusMeta.text}.</b>{' '}
						Tốc độ chi {fmtMoney(budget.burn_month_cost, cur)}/tháng (TB {fmtMoney(budget.avg_month_cost, cur)} · dự phóng tháng này {fmtMoney(budget.forecast_month_cost, cur)});
						ngưỡng an toàn = {budget.safe_months} tháng + {budget.buffer_pct}% dự phòng.{' '}
						<Typography.Link onClick={onEdit}>Cập nhật số dư</Typography.Link>
					</span>
				}
			/>
		</>
	);
}

/** Bảng chỉ số của 1 dịch vụ: cột theo tháng + hạn mức + tháng này + chi phí. */
function ServiceTable({service, months}) {
	const current = months[months.length - 1];

	const columns = [
		{
			title: 'Chỉ số', key: 'label', fixed: 'left',
			render: (_, r) => (
				<div>
					<div style={{fontWeight: 600}}>{r.label}</div>
					<div style={{color: '#9ca3af', fontSize: 12}}>{r.unit_cost_label}</div>
				</div>
			),
		},
		...months.map((m) => ({
			title: monthLabel(m), key: m, align: 'right',
			render: (_, r) => {
				const v = r.series?.[m] ?? 0;
				return <span style={m === current ? {fontWeight: 700} : undefined}>{v >= 100000 ? compact(v) : nf(v)}</span>;
			},
		})),
		{
			title: 'Hạn mức / tháng', key: 'quota', align: 'right',
			render: (_, r) => r.quota_month ? nf(r.quota_month) : <span style={{color: '#9ca3af'}}>—</span>,
		},
		{
			title: 'Tháng này', key: 'used', align: 'right',
			render: (_, r) => {
				const over = r.quota_month && r.used_month > r.quota_month;
				const pct = r.quota_month ? Math.round(r.used_month / r.quota_month * 100) : null;
				return (
					<span style={{fontWeight: 700, color: over ? '#dc2626' : undefined}}>
						{nf(r.used_month)}
						{pct !== null && <span style={{color: over ? '#dc2626' : '#9ca3af', fontSize: 12, fontWeight: 400}}> ({pct}%)</span>}
					</span>
				);
			},
		},
		{
			title: 'Chi phí tháng này', key: 'cost', align: 'right',
			render: (_, r) => r.cost_month !== null && r.cost_month !== undefined
				? fmtMoney(r.cost_month, service.currency)
				: <Tag color="green" style={{marginRight: 0}}>Miễn phí</Tag>,
		},
	];

	return (
		<Table
			columns={columns}
			dataSource={service.metrics}
			rowKey="key"
			pagination={false}
			size="small"
			scroll={{x: 'max-content'}}
			locale={{emptyText: 'Chưa có dữ liệu'}}
			summary={service.cost_series ? () => (
				<Table.Summary.Row>
					<Table.Summary.Cell index={0}><b>Tổng chi phí (ước tính)</b></Table.Summary.Cell>
					{months.map((m, i) => (
						<Table.Summary.Cell index={i + 1} key={m} align="right">
							<b>{fmtMoney(service.cost_series[m], service.currency)}</b>
						</Table.Summary.Cell>
					))}
					<Table.Summary.Cell index={months.length + 1} />
					<Table.Summary.Cell index={months.length + 2} />
					<Table.Summary.Cell index={months.length + 3} align="right">
						<b>{fmtMoney(service.cost_month, service.currency)}</b>
					</Table.Summary.Cell>
				</Table.Summary.Row>
			) : undefined}
		/>
	);
}

/** Nhãn ô nhập số dư theo dịch vụ (đơn vị tiền của tài khoản đó). */
const BUDGET_FIELDS = [
	{key: 'ai', label: 'Tài khoản AI (khoá chung)', currency: 'VND'},
	{key: 'dataforseo', label: 'Tài khoản DataForSEO', currency: 'USD'},
	{key: 'email', label: 'Tài khoản Email (Brevo...)', currency: 'USD'},
];

function ServiceQuotas() {
	const {notification} = AntdApp.useApp();
	const [monthCount, setMonthCount] = useState(6);
	const {data, isLoading, isError} = useGetServiceQuotasQuery({months: monthCount});
	const [updateBudgets, {isLoading: saving}] = useUpdateServiceBudgetsMutation();

	const [budgetOpen, setBudgetOpen] = useState(false);
	const budgetForm = useForm({defaultValues: {safe_months: 1}});

	// Nạp số dư đã lưu mỗi khi mở modal (date BE 'YYYY-MM-DD' → DateField nhận string ok).
	useEffect(() => {
		if (budgetOpen && data?.budgets_input) budgetForm.reset(data.budgets_input);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [budgetOpen, data]);

	const submitBudgets = async (values) => {
		const body = {safe_months: values.safe_months || 1};
		BUDGET_FIELDS.forEach(({key}) => {
			const row = values[key] || {};
			body[key] = {
				amount: row.amount || 0,
				// DateField trả timestamp ms khi user chọn lại; giữ nguyên string cũ nếu không đổi.
				date: row.date ? dayjs(row.date).format('YYYY-MM-DD') : null,
			};
		});
		try {
			await updateBudgets(body).unwrap();
			notification.success({message: 'Đã lưu số dư đầu kỳ'});
			setBudgetOpen(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	if (isError) {
		return (
			<div className="container">
				<PageHeader icon="fa-light fa-plug" title="Dịch vụ bên thứ 3" />
				<Alert type="error" showIcon message="Không tải được số liệu dịch vụ." />
			</div>
		);
	}

	if (isLoading || !data) {
		return (
			<div className="container">
				<PageHeader icon="fa-light fa-plug" title="Dịch vụ bên thứ 3" />
				<Loading />
			</div>
		);
	}

	const {months, services} = data;

	return (
		<div className="container">
			<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap'}}>
				<PageHeader
					icon="fa-light fa-plug"
					title="Dịch vụ bên thứ 3"
					subtitle={`Hạn mức & mức sử dụng theo tháng của các nhà cung cấp · cập nhật ${dayjs(data.generated_at).format('HH:mm DD/MM/YYYY')}`}
				/>
				<div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
					<Select
						value={monthCount}
						onChange={setMonthCount}
						options={[{value: 3, label: '3 tháng'}, {value: 6, label: '6 tháng'}, {value: 12, label: '12 tháng'}]}
						style={{width: 110}}
					/>
					<Button white leftIcon={<FontAwesomeIcon icon="fa-light fa-piggy-bank" />} onClick={() => setBudgetOpen(true)}>
						Số dư đầu kỳ
					</Button>
				</div>
			</div>

			{services.map((s) => (
				<div key={s.key} style={{marginBottom: 8}}>
					<div className={cn('section-title')} style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
						<FontAwesomeIcon icon={SERVICE_ICONS[s.key] || 'fa-light fa-plug'} />
						{s.name}
						<Tag color={s.free ? 'green' : 'blue'} style={{marginLeft: 4}}>{s.free ? 'Miễn phí' : 'Trả phí'}</Tag>
						<span style={{color: '#9ca3af', fontSize: 12, fontWeight: 400}}>{s.vendor}</span>
						{!s.free && s.cost_month !== null && (
							<span style={{marginLeft: 'auto', fontSize: 13, fontWeight: 600}}>
								Chi tháng này: {fmtMoney(s.cost_month, s.currency)}
							</span>
						)}
					</div>

					<BudgetPanel budget={s.budget} onEdit={() => setBudgetOpen(true)} />

					<ServiceTable service={s} months={months} />

					<div style={{color: '#9ca3af', fontSize: 12, margin: '6px 0 20px'}}>{s.note}</div>
				</div>
			))}

			<div style={{color: '#9ca3af', fontSize: 12}}>
				Chi phí ước tính theo đơn giá ở <Link to="/admin/plans">Quản lý gói → Tham số giá vốn</Link>.
				Số liệu đếm theo lượt gọi THẬT tới nhà cung cấp (cache dùng chung không tính) — có thể chênh nhẹ
				so với hoá đơn thực tế của nhà cung cấp.
			</div>

			{/* Số dư đầu kỳ từng tài khoản + số tháng an toàn (Option BE omni_service_budgets). */}
			<ModalForm
				open={budgetOpen}
				icon="fa-light fa-piggy-bank"
				title="Số dư đầu kỳ tài khoản dịch vụ"
				subtitle="Nhập số tiền đang có trong từng tài khoản + ngày nạp. Hệ thống tự tính đã chi từ ngày đó, còn lại bao nhiêu và cần nạp thêm bao nhiêu để đủ số tháng an toàn."
				onCancel={() => setBudgetOpen(false)}
				onOk={budgetForm.handleSubmit(submitBudgets)}
				okText="Lưu"
				loading={saving}
				width={560}
			>
				<Controller control={budgetForm.control} name="safe_months" render={({field}) => (
					<NumberField label="Số tháng an toàn (muốn đảm bảo đủ chi phí)" min={1} max={12} {...field} />
				)} />
				{BUDGET_FIELDS.map(({key, label, currency}) => (
					<div key={key}>
						<Divider orientation="left" style={{fontSize: 13}}>{label}</Divider>
						<Controller control={budgetForm.control} name={`${key}.amount`} render={({field}) => (
							<NumberField label={`Số dư đầu kỳ (${currency})`} min={0} step={currency === 'USD' ? 1 : 100000} {...field} />
						)} />
						<Controller control={budgetForm.control} name={`${key}.date`} render={({field}) => (
							<DateField label="Ngày nạp / chốt số dư" {...field} />
						)} />
					</div>
				))}
				<div style={{color: '#9ca3af', fontSize: 12}}>
					Đặt 0 / bỏ trống ngày = tắt chuẩn đoán cho tài khoản đó. Nạp thêm tiền thì cập nhật lại
					số dư mới + ngày nạp mới (hệ thống tính "đã chi" từ ngày này).
				</div>
			</ModalForm>
		</div>
	);
}

export default ServiceQuotas;
